import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';

import { Badge, Card, CardContent, CardHeader } from '@/components/ui';
import { adminApi } from '@/lib/api';

/**
 * Platform Administration — user management (Spec S10).
 *
 * Phase 6 MVP: read-only view backed by the `/api/v1/admin/audit` endpoint
 * filtered to `user_invitation_requested`. Full CRUD lives in Keycloak —
 * the backend records every invitation so this page can mirror state.
 *
 * Phase 7+ will add the create-user wizard and direct Keycloak integration.
 */
export function AdminUsers() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', 'invitations'],
    queryFn: () =>
      adminApi.audit({
        event_type: 'user_invitation_requested',
        page: 1,
        page_size: 100,
      }),
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-display text-brand-900">Users</h1>
          <p className="mt-1 text-body text-slate-600">
            Invitations recorded by the platform. User edits land in Keycloak.
          </p>
        </div>
        <Badge variant="info">{data ? `${data.total} invitations` : '—'}</Badge>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">Recent invitations</h2>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="px-4 py-3 text-small text-slate-500">Loading…</p>}
          {error && (
            <p className="px-4 py-3 text-small text-red-700">
              <ShieldAlert className="mr-1 inline h-3.5 w-3.5" aria-hidden />
              Failed to load users.
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="px-4 py-3 text-small text-slate-500">No invitations yet.</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-body">
              <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Tier</th>
                  <th className="px-4 py-2 text-left">Invited at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((evt) => {
                  const meta = (evt.metadata ?? {}) as Record<string, unknown>;
                  return (
                    <tr key={evt.event_id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-brand-900">
                        {String(meta.email ?? evt.resource_id ?? '—')}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{String(meta.role ?? '—')}</td>
                      <td className="px-4 py-2 text-slate-700">{String(meta.tier ?? '—')}</td>
                      <td className="px-4 py-2 text-small text-slate-500">
                        {new Date(evt.created_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
